import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { FolderOpen, Trash2, User, MessageCircle, Send } from "lucide-react";

const isAnamnesiDiet = (d) => d.fromAnamnesi || /^Anamnesi\s—/.test(d.nome || "");
const hasDiet = (p) =>
  (p.diets || []).some((d) => !isAnamnesiDiet(d) && (d.meals || []).some((m) => (m.foods || []).length > 0));
const hasWorkout = (p) =>
  ((p.allenamento && p.allenamento.schede) || []).some((s) => (s.esercizi || []).some((ex) => (ex.superset ? (ex.a && ex.a.nome) || (ex.b && ex.b.nome) : ex.nome)));

export default function PatientsDrawer({ open, setOpen, patients, onLoad, onDelete, currentId }) {
  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent side="right" className="w-full border-white/10 bg-[hsl(var(--card))] text-foreground sm:max-w-md" data-testid="patients-drawer">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2 font-display text-foreground">
            <User className="h-5 w-5 text-primary" /> Pazienti Salvati
            <span className="ml-1 rounded-md bg-primary/15 px-2 py-0.5 text-xs font-bold text-primary">{patients.length}</span>
          </SheetTitle>
        </SheetHeader>

        <div className="mt-5 space-y-2 overflow-y-auto pb-6" style={{ maxHeight: "calc(100vh - 120px)" }}>
          {patients.length === 0 && (
            <div className="rounded-lg border border-dashed border-white/10 py-10 text-center text-sm text-muted-foreground">Nessun paziente salvato</div>
          )}
          {patients.map((p) => {
            const full = [p.cognome, p.nome].filter(Boolean).join(" ") || "Senza nome";
            const dietOk = hasDiet(p);
            const workoutOk = hasWorkout(p);
            const complete = dietOk && workoutOk;
            const dotTitle = complete ? "Dieta e allenamento salvati" : `Manca: ${[!dietOk && "dieta", !workoutOk && "allenamento"].filter(Boolean).join(" e ")}`;
            const phone = (p.anamnesi && p.anamnesi.generali && p.anamnesi.generali.cellulare) || "";
            const digits = phone.replace(/[^\d]/g, "");
            return (
              <div key={p.id} className={`flex items-center justify-between gap-2 rounded-lg border px-3 py-2.5 transition-colors ${p.id === currentId ? "border-primary/50 bg-primary/10" : "border-white/10 bg-black/20 hover:border-white/20"}`} data-testid={`patient-item-${p.id}`}>
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span
                      className="h-2.5 w-2.5 shrink-0 rounded-full"
                      style={{ background: complete ? "hsl(142 71% 45%)" : "hsl(0 72% 55%)" }}
                      title={dotTitle}
                      data-complete={complete ? "true" : "false"}
                      data-testid={`patient-diet-dot-${p.id}`}
                    />
                    <div className="truncate font-semibold">{full}</div>
                  </div>
                  <div className="mt-1 space-y-1 text-xs text-muted-foreground">
                    {p.mestiere && <div data-testid={`patient-mestiere-${p.id}`}>{p.mestiere}</div>}
                    {digits ? (
                      <div className="flex items-center gap-2" data-testid={`patient-phone-${p.id}`}>
                        <span className="font-mono-num">{phone}</span>
                        <a href={`https://wa.me/${digits}`} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} title="Chatta su WhatsApp" data-testid={`patient-wa-${p.id}`} className="inline-flex text-[hsl(142_71%_45%)] transition-opacity hover:opacity-70"><MessageCircle className="h-4 w-4" /></a>
                        <a href={`tg://resolve?phone=${digits}`} onClick={(e) => e.stopPropagation()} title="Chatta su Telegram" data-testid={`patient-tg-${p.id}`} className="inline-flex text-[hsl(200_80%_55%)] transition-opacity hover:opacity-70"><Send className="h-4 w-4" /></a>
                      </div>
                    ) : (
                      <div className="italic opacity-60" data-testid={`patient-no-phone-${p.id}`}>Nessun numero di telefono</div>
                    )}
                  </div>
                </div>
                <div className="flex shrink-0 items-center gap-1">
                  <button onClick={() => onLoad(p)} className="rounded-md border border-white/10 p-2 text-muted-foreground transition-colors hover:text-primary" title="Carica" data-testid={`load-patient-${p.id}`}><FolderOpen className="h-4 w-4" /></button>
                  <button onClick={() => onDelete(p)} className="rounded-md border border-white/10 p-2 text-muted-foreground transition-colors hover:text-destructive" title="Elimina" data-testid={`delete-patient-${p.id}`}><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>
            );
          })}
        </div>
      </SheetContent>
    </Sheet>
  );
}
